import React, { useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { z } from 'zod';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import Switch from 'components/Switch/Switch';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';

import {
  createColorSetValueHandler,
  OPACITY_OPTIONS
} from '../../../utils/blockForms.utils';
import { templateStudioSchema } from '../../../validation-schema';
import { DesignBlockMenuItem } from '../Components/DesignBlockMenuItem';

import './style.css';

export const TemplateStudioHeaderForm: React.FC = () => {
  const {
    control,
    setValue,
    watch,
    formState: { errors, touchedFields }
  } = useFormContext<z.infer<typeof templateStudioSchema>>();

  const headerOpacity = watch('header.backgroundOpacity');
  const defaultSelectedOpacity = useMemo(
    () =>
      OPACITY_OPTIONS.find((option) => option.value === (headerOpacity ?? 100)),
    [headerOpacity]
  );

  const colorSetValueHandler = (onChange: (value: any) => void) =>
    createColorSetValueHandler(
      'header.textColor',
      () => watch('header.textColor'),
      setValue as (name: string, value: any) => void,
      onChange
    );

  const hasTextColorError =
    touchedFields.header?.textColor && Boolean(errors.header?.textColor);
  const textColorHelperText = touchedFields.header?.textColor
    ? errors.header?.textColor?.toString()
    : '';

  return (
    <DesignBlockMenuItem blockType={'header'}>
      <FormField
        control={control}
        name="header.showPlayerName"
        render={({ field }) => (
          <Switch
            key={field.value ? 'on' : 'off'}
            label="Show Player Name"
            textAlignment="left"
            defaultChecked={field.value}
            checked={field.value}
            onCheckedChange={field.onChange}
          />
        )}
      />
      <FormField
        control={control}
        name="header.showPlayerId"
        render={({ field }) => (
          <Switch
            key={field.value ? 'on' : 'off'}
            label="Show Player ID"
            textAlignment="left"
            defaultChecked={field.value}
            checked={field.value}
            onCheckedChange={field.onChange}
          />
        )}
      />
      <FormField
        control={control}
        name="header.isSticky"
        render={({ field }) => (
          <Switch
            key={field.value ? 'on' : 'off'}
            label="Sticky Header"
            textAlignment="left"
            defaultChecked={field.value}
            checked={field.value}
            onCheckedChange={field.onChange}
          />
        )}
      />
      <FormField
        control={control}
        name="header.backgroundOpacity"
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <FormControl>
              <InputSelectDropdown
                label="Background Opacity"
                options={OPACITY_OPTIONS}
                defaultSelected={defaultSelectedOpacity}
                onOptionChange={(selectedValue) =>
                  onChange(Number(selectedValue))
                }
                placeholder="Select opacity"
                isHorizontal={true}
                className="[&>div:last-child]:w-[102px] justify-between items-center"
                buttonClassName={'h-[30px]'}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="header.textColor"
        control={control}
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Text color</label>
              <FormControl>
                <div className="[&_.MuiInputAdornment-root]:mr-0 [&_.MuiTextField-root]:!w-[102px] [&_.MuiInputBase-input]:!px-3 [&_.MuiInputBase-input]:!py-2 [&_.MuiInputBase-input]:!pl-0">
                  <AcGradientInput
                    name="header.textColor"
                    defaultValue={value || { colorOne: '#FFF' }}
                    setValue={colorSetValueHandler(onChange)}
                    onlySolid={true}
                    circular={true}
                    imgWidth="16px"
                    imgHeight="16px"
                    error={hasTextColorError}
                    helperText={textColorHelperText}
                  />
                </div>
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </DesignBlockMenuItem>
  );
};
